import { CommonModule } from '@angular/common';
import { booleanAttribute, Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CalendarModule } from 'primeng/calendar';
import { ButtonComponent } from '../button/button.component';
import { ButtonRadiusEnum } from '../button/types/button-radius.enum';
import { ButtonTypeEnum } from '../button/types/button-type.enum';
import { IconComponent } from '../icon/icon.component';
import { OutsideClick } from '../../directives/outside-click/outside-click.directive';
import { CourseStatus } from '../../models/enums/course-status.enum';
import { Category } from '../../models/interfaces/category.interface';
import { CourseFilterParams, CourseStatusFilter } from '../../models/interfaces/course-filter-value.interface';

@Component({
  selector: 'l-course-filter',
  standalone: true,
  templateUrl: './course-filter.component.html',
  imports: [
    CommonModule,
    FormsModule,
    CalendarModule,
    ButtonComponent,
    IconComponent,
    OutsideClick,
  ],
})
export class CourseFilterComponent implements OnInit, OnChanges {

  @Input({ required: true })
  public categories: Category[] = [];

  @Input()
  public selectedStatus?: CourseStatusFilter = 'all';

  @Input()
  public selectedCategories?: Category[] = [];

  @Input()
  public selectedDateRange?: [Date | null, Date | null];

  @Input({ transform: booleanAttribute })
  public categoryDisabled = false;

  @Input({ transform: booleanAttribute })
  public dateDisabled = false;

  @Output()
  public filterApplied = new EventEmitter<CourseFilterParams>();

  public open = false;

  public categoryDropdownOpen = false;

  public draftSelectedStatus?: CourseStatusFilter = 'all';

  public draftSelectedCategories?: Category[] = [];

  public draftDateRange?: [Date | null, Date | null];

  public readonly statusOptions: Array<{ value: CourseStatusFilter; label: string; icon: string }> = [
    { value: 'all', label: 'Todos', icon: 'auto_awesome' },
    { value: CourseStatus.ACTIVE, label: 'Ativo', icon: 'check' },
    { value: CourseStatus.INACTIVE, label: 'Inativo', icon: 'close' },
    { value: CourseStatus.DRAFT, label: 'Rascunho', icon: 'edit' },
  ];

  public readonly buttonTypeEnum = ButtonTypeEnum;

  public readonly buttonRadiusEnum = ButtonRadiusEnum;

  private previousSelectedStatus?: CourseStatusFilter = 'all';

  private previousSelectedCategories?: Category[] = [];

  private previousDateRange?: [Date | null, Date | null];

  public ngOnInit(): void {
    this.syncDrafts();
  }

  public ngOnChanges(): void {
    if (!this.open) {
      this.syncDrafts();
    }
  }

  public toggleOpen(): void {
    if (this.open) {
      this.close();
      return;
    }

    this.syncDrafts();
    this.open = true;
  }

  public close(): void {
    this.restorePreviousState();
    this.categoryDropdownOpen = false;
    this.open = false;
  }

  public clearFilter(): void {
    this.draftSelectedStatus = 'all';
    this.draftSelectedCategories = [];
    this.draftDateRange = undefined;
  }

  public applyFilter(): void {
    this.selectedStatus = this.draftSelectedStatus;
    this.selectedCategories = this.draftSelectedCategories ? [...this.draftSelectedCategories] : [];
    this.selectedDateRange = this.draftDateRange ? [...this.draftDateRange] as [Date | null, Date | null] : undefined;

    this.filterApplied.emit({
      status: this.selectedStatus,
      categories: [...this.selectedCategories],
      createdAtRange: this.selectedDateRange,
    });

    this.open = false;
    this.categoryDropdownOpen = false;
    this.syncDrafts();
  }

  public selectStatus(status: CourseStatusFilter): void {
    this.draftSelectedStatus = status;
  }

  public toggleCategoryDropdown(): void {
    if (this.categoryDisabled) {
      return;
    }

    this.categoryDropdownOpen = !this.categoryDropdownOpen;
  }

  public selectCategory(category: Category): void {
    if (this.categoryDisabled) {
      return;
    }

    const isSelected = this.draftSelectedCategories?.some((item) => item.id === category.id) || false;

    if (isSelected) {
      this.draftSelectedCategories = this.draftSelectedCategories?.filter((item) => item.id !== category.id) || [];
      return;
    }

    this.draftSelectedCategories = this.draftSelectedCategories ? [...this.draftSelectedCategories, category] : [category];
  }

  public clearSelectedCategories(): void {
    if (this.categoryDisabled) {
      return;
    }

    this.draftSelectedCategories = [];
  }

  public getCategoryLabel(): string {
    if (!this.draftSelectedCategories || this.draftSelectedCategories.length === 0) {
      return 'Todos';
    }

    if (this.draftSelectedCategories.length === 1) {
      return this.draftSelectedCategories[0].name;
    }

    return `${this.draftSelectedCategories.length} categorias`;
  }

  public isCategorySelected(categoryId: string): boolean {
    return this.draftSelectedCategories?.some((category) => category.id === categoryId) || false;
  }

  public isSelected(status: CourseStatusFilter): boolean {
    return this.draftSelectedStatus === status;
  }

  private syncDrafts(): void {
    this.previousSelectedStatus = this.selectedStatus;
    this.previousSelectedCategories = this.selectedCategories ? [...this.selectedCategories] : [];
    this.previousDateRange = this.selectedDateRange ? [...this.selectedDateRange] as [Date | null, Date | null] : undefined;

    this.draftSelectedStatus = this.selectedStatus;
    this.draftSelectedCategories = this.selectedCategories ? [...this.selectedCategories] : [];
    this.draftDateRange = this.selectedDateRange ? [...this.selectedDateRange] as [Date | null, Date | null] : undefined;
  }

  private restorePreviousState(): void {
    this.selectedStatus = this.previousSelectedStatus;
    this.selectedCategories = this.previousSelectedCategories ? [...this.previousSelectedCategories] : [];
    this.selectedDateRange = this.previousDateRange ? [...this.previousDateRange] as [Date | null, Date | null] : undefined;

    this.draftSelectedStatus = this.previousSelectedStatus;
    this.draftSelectedCategories = this.previousSelectedCategories ? [...this.previousSelectedCategories] : [];
    this.draftDateRange = this.previousDateRange ? [...this.previousDateRange] as [Date | null, Date | null] : undefined;
  }

}